import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/auth.css';
import { getApiUrl, getCsrfToken } from '../utils/apiHelper';
import { validatePasswordStrength } from '../utils/passwordValidation';

export default function Signup() {
  const [formData, setFormData] = useState({
    license_key: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEula, setShowEula] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Limit first_name and last_name to 50 characters
    if (name === 'first_name' || name === 'last_name') {
      if (value.length <= 50) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    setError(''); // Clear error when user types
  };

  const validateForm = () => {
    // Validate license key - not only spaces
    if (formData.license_key && formData.license_key.trim() === '') {
      setError('Please enter your license key');
      return false;
    }

    // Validate first name - not only spaces, only alphabetic characters and length
    if (formData.first_name && formData.first_name.trim() === '') {
      setError('Please enter your first name');
      return false;
    }
    if (formData.first_name && !/^[a-zA-Z\s]+$/.test(formData.first_name.trim())) {
      setError('First name can only contain letters');
      return false;
    }
    if (formData.first_name && formData.first_name.length > 50) {
      setError('First name must be 50 characters or less');
      return false;
    }

    // Validate last name - not only spaces, only alphabetic characters and length
    if (formData.last_name && formData.last_name.trim() === '') {
      setError('Please enter your last name');
      return false;
    }
    if (formData.last_name && !/^[a-zA-Z\s]+$/.test(formData.last_name.trim())) {
      setError('Last name can only contain letters');
      return false;
    }
    if (formData.last_name && formData.last_name.length > 50) {
      setError('Last name must be 50 characters or less');
      return false;
    }

    const passwordError = validatePasswordStrength(formData.password);
    if (passwordError) {
      setError(passwordError);
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const performSignup = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Get CSRF token first
      const csrfToken = await getCsrfToken();

      const { confirmPassword, first_name, last_name, ...submitData } = formData; // Remove confirmPassword and rename fields

      // Map frontend field names to backend expected names
      const backendData = {
        ...submitData,
        fname: first_name,
        lname: last_name
      };

      const response = await fetch(getApiUrl('/api/internal/auth/signup/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include', // Include cookies for session
        body: JSON.stringify(backendData),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Account created successfully! Redirecting to login...');

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(data.message || 'Signup failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
      setShowEula(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Show EULA modal; user confirms there before API is called
    setEulaAccepted(false);
    setShowEula(true);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Sign Up</h2>
          <p>Create your account with a valid license key</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-group">
            <label htmlFor="license_key">License Key</label>
            <input
              type="text"
              id="license_key"
              name="license_key"
              value={formData.license_key}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter your license key"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Enter your first name"
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Enter your last name"
                maxLength={50}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Minimum 6 characters"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Confirm your password"
              />
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <a href="/login" className="auth-link">Sign in</a>
          </p>
        </div>

        {showEula && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15,23,42,0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
          >
            <div
              style={{
                background: 'var(--card-bg, #020617)',
                color: 'var(--card-text, #e5e7eb)',
                borderRadius: '12px',
                boxShadow: '0 20px 60px rgba(15,23,42,0.7)',
                maxWidth: '640px',
                width: '90%',
                padding: '24px 28px',
                boxSizing: 'border-box',
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '12px',
                }}
              >
                End User License Agreement
              </h3>
              <div
                style={{
                  maxHeight: '260px',
                  overflowY: 'auto',
                  padding: '12px 16px',
                  paddingRight: '12px',
                  marginBottom: '16px',
                  border: '1px solid var(--card-border, #1f2937)',
                  borderRadius: '8px',
                  background: 'rgba(15,23,42,0.9)',
                  boxSizing: 'border-box',
                  fontSize: '12px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                <strong>WHITEHAX - SOFTWARE-AS-A-SERVICE LICENSE AND SERVICES AGREEMENT</strong>
                {'\n'}
                {'\n'}
                PLEASE READ THIS SOFTWARE-AS-A-SERVICE AND SOFTWARE USAGE LICENSE AGREEMENT CAREFULLY. BY DOWNLOADING,
                INSTALLING OR USING THE SOFTWARE YOU INDICATE ACCEPTANCE OF AND AGREE TO THE TERMS AND CONDITIONS OF THIS
                AGREEMENT. IF YOU DO NOT AGREE TO THESE TERMS AND CONDITIONS, DO NOT DOWNLOAD, INSTALL OR USE THE
                SOFTWARE. THIS AGREEMENT DOES NOT SUPERCEDE ANY OTHER WRITTEN AGREEMENT BETWEEN YOU AND IronSDN, Corp.
                {'\n'}
                {'\n'}
                <strong>Effective Date:</strong> As of the first day of registration or usage.
                {'\n'}
                {'\n'}
                This Software-as-a-Service License and Services Agreement ("Agreement") is entered into by and between:
                {'\n'}
                IronSDN, Corp., a California corporation ("Company"), and the entity or individual on behalf of the entity
                accepting this Agreement ("Customer" or "Licensee").
                {'\n'}
                By accessing or using the WhiteHaX platform, Customer agrees to be bound by the terms of this Agreement.
                {'\n'}
                {'\n'}
                <strong>1. DEFINITIONS</strong>
                {'\n'}
                "Software" or "Platform" means the WhiteHaX AI-ASM platform, including cloud-hosted services, attack
                surface management tools, AI security testing modules, agentic-AI validation systems, APIs, associated
                software agents, documentation and updates.
                {'\n'}
                "Agentic-AI System" means any artificial intelligence model, autonomous agent, large language model,
                orchestration framework, or machine learning system tested using the Platform.
                {'\n'}
                "Authorized Users" means employees or contractors of the Customer, authorized by Customer to access the
                Platform.
                {'\n'}
                "Customer Environment" means Customer’s systems, networks, applications, APIs, infrastructure, or AI
                systems submitted for testing.
                {'\n'}
                "Security Testing" means automated and manual tests and simulations designed to identify vulnerabilities,
                misconfigurations, adversarial behaviors, model exploitation vectors, and attack surface exposure.
                {'\n'}
                "Readiness Data" means telemetry, metadata, vulnerability findings, testing results, recommendations and
                security posture metrics generated during testing.
                {'\n'}
                "Subscription Term" Means the period during which Customer and Authorized Users are allowed to use the
                Software.
                {'\n'}
                {'\n'}
                <strong>2. LICENSE GRANT</strong>
                {'\n'}
                Subject to compliance with this Agreement and payment of applicable fees, Company grants Customer a
                limited, non-exclusive, non-transferable, non-sublicensable, subscription-based license, to access and use
                the Platform during the Subscription Term solely for Customer’s internal security testing purposes. This
                license is not a sale. Title and copyrights to the Software, in whole and in part and all copies thereof,
                and all modifications, enhancements, derivatives and other alterations of the Software regardless of who
                made any modifications, if any, are, and will remain, the sole and exclusive property of Company. Company
                reserves any rights not expressly granted to Licensee. Company is not obligated to provide and Licensee
                acquires no right of any kind with respect to any source code for the Software. Licensee agrees that it
                has no right whatsoever to modify the Software or any portion thereof in any manner. Licensee shall not,
                nor permit any third party to reverse engineer, decompile, disassemble or otherwise reduce the Software to
                any other humanly perceivable form, and may not modify, adapt, rent, lease, loan or create derivative
                works based upon the Software or any part thereof. Customer agrees to allow access only to Authorized
                Users and Customer is responsible for all activity conducted through its accounts.
                {'\n'}
                {'\n'}
                <strong>3. SUBSCRIPTION AND USAGE LIMITS</strong>
                {'\n'}
                Use of the Platform may be limited by number of users, number of scanned assets, number of AI systems
                tested, API usage, compute usage, number of scans, number of tests, storage limits as defined in the
                applicable Order Form. Exceeding licensed limits may result in additional charges, rate limiting or
                suspension of service.
                {'\n'}
                {'\n'}
                <strong>4. AUTHORIZATION FOR SECURITY TESTING</strong>
                {'\n'}
                Customer represents and warrants that it possesses all necessary legal authority and permissions to
                authorize security testing of any system submitted to the Platform. Customer shall not submit systems for
                testing unless it owns the system, or has explicit written authorization from the owner. Customer agrees
                to indemnify and hold harmless Company from any claims arising from testing activities conducted against
                systems without proper authorization.
                {'\n'}
                {'\n'}
                <strong>5. AI AND AGENTIC SYSTEM TESTING</strong>
                {'\n'}
                The Platform may test and simulate adversarial behaviors including but not limited to prompt injection,
                model manipulation, adversarial input generation, malicious doc uploads, AI specific denial-of-service,
                agent privilege escalation, AI workflow exploitation, API abuse, MCP exploits, model extraction attempts,
                multi-agent coordination attacks etc. Customer acknowledges that such tests and simulations may generate
                abnormal traffic, trigger security alarms, temporarily affect system performance or cause other
                detrimental effects to Customer systems. Company recommends that Customer use staging or other
                non-production deployment for testing using the Platform.
                {'\n'}
                {'\n'}
                <strong>6. ACCEPTABLE USE POLICY</strong>
                {'\n'}
                Customer shall not use the Platform to conduct unauthorized penetration testing, attack third-party
                systems without consent, launch denial-of-service attacks on third-party systems, deploy malware, attempt
                to breach Company infrastructure, develop or train competing security products, benchmark the Platform for
                competitive publication, circumvent usage limits, or conduct illegal activity. Violation may result in
                immediate suspension or termination.
                {'\n'}
                {'\n'}
                <strong>7. INTELLECTUAL PROPERTY</strong>
                {'\n'}
                The Platform and all associated technology are the exclusive property of Company. This includes software,
                AI models, security testing algorithms, vulnerability databases, threat intelligence, attack simulation
                frameworks. No ownership rights are transferred.
                {'\n'}
                {'\n'}
                <strong>8. AI MODEL AND ALGORITHM PROTECTION</strong>
                {'\n'}
                Customer shall not reverse engineer, extract tests or training data, replicate testing methodologies,
                derive attack models, recreate AI detection systems, train competing models using Platform outputs. Any
                attempt to extract model behavior, test data, training data, or algorithmic logic is strictly prohibited.
                {'\n'}
                {'\n'}
                <strong>9. DATA COLLECTION AND SECURITY</strong>
                {'\n'}
                During use of the Platform, Company may collect system telemetry, vulnerability findings, configuration
                data, testing metadata, Platform generated recommendations attack simulation results. Company may use
                aggregated and anonymized data to improve detection algorithms, train machine learning models, develop
                additional tests, develop security intelligence and produce benchmarking insights. Company will not
                disclose identifiable Customer information without consent. Customer retains ownership of Customer
                Environment data.
                {'\n'}
                {'\n'}
                <strong>10. CUSTOMER RESPONSIBILITIES</strong>
                {'\n'}
                Customer is responsible for securing its own infrastructure, reviewing security findings, applying
                remediation, maintaining backup systems, ensuring compliance with applicable laws and all other operations
                to protect itself, its intellectual and physical properties.
                {'\n'}
                {'\n'}
                <strong>11. SECURITY DISCLAIMER</strong>
                {'\n'}
                Security testing may miss or not identify all vulnerabilities. The Platform does not guarantee complete
                security, does not eliminate cyber risk, does not replace security professionals or is a substitute for
                good security hygiene and practices. Customer acknowledges that results, telemetries, vulnerability
                findings or Platform generated recommendations are advisory.
                {'\n'}
                {'\n'}
                <strong>12. SERVICE AVAILABILITY</strong>
                {'\n'}
                Company will use commercially reasonable efforts to maintain service availability. However, service
                interruptions may occur due to maintenance, infrastructure failures, cyber attacks, third-party cloud
                outages, software failures, force majeure events or any other unforeseen events.
                {'\n'}
                {'\n'}
                <strong>13. LIMITATION OF LIABILITY, REMEDIES AND DAMAGES</strong>
                {'\n'}
                IN NO EVENT WILL COMPANY, ITS SUBSIDIARIES OR ANY OF THE LICENSORS, DIRECTORS, OFFICERS, EMPLOYEES OR
                AFFILIATES OF ANY OF THE FOREGOING BE LIABLE TO CUSTOMER UNDER ANY CONTRACT, NEGLIGENCE, STRICT LIABILITY
                OR OTHER LEGAL OR EQUITABLE THEORY FOR ANY CONSEQUENTIAL, INCIDENTAL, INDIRECT OR SPECIAL DAMAGES
                WHATSOEVER (INCLUDING, BUT NOT LIMITED TO, DAMAGES FOR SECURITY BREACHES OR INCIDENTS, SYSTEM DEGRADATION
                OR DOWNTIME, LOSS OF BUSINESS PROFITS, BUSINESS INTERRUPTION, LOSS OF BUSINESS DATA OR INFORMATION AND THE
                LIKE), WHETHER FORESEEABLE OR UNFORESEEABLE, OR FOR COST OF PROCUREMENT OF SUBSTITUTE GOODS, TECHNOLOGY OR
                SERVICES, REGARDLESS OF THE BASIS OF THE CLAIM AND EVEN IF COMPANY OR A COMPANY REPRESENTATIVE HAS BEEN
                ADVISED OF THE POSSIBILITY OF SUCH DAMAGE. COMPANY'S CUMULATIVE LIABILITY FOR DAMAGES FOR ANY CAUSE
                WHATSOEVER, AND REGARDLESS OF THE FORM OF THE ACTION, WILL BE LIMITED TO NO GREATER THAN THE AMOUNT OF
                MONEY PAID TO COMPANY, PRORATED BY THE NUMBER OF MONTHS REMAINING IN THE SUBSCRIPTION TERM.
                {'\n'}
                {'\n'}
                <strong>14. CONFIDENTIALITY</strong>
                {'\n'}
                "Confidential Information" shall be defined to include Software, source code, object code, documentation,
                vulnerability findings, security reports and any proprietary tools, proprietary knowledge or proprietary
                methodologies disclosed by Company to Customer under or relating to this Agreement. Licensee shall observe
                complete confidentiality with respect to the Confidential Information, and shall use its best efforts and
                take all reasonable steps to protect the Confidential Information from any use, reproduction, publication,
                disclosure, or distribution except as specifically authorized by this Agreement. Licensee shall promptly
                notify Company of any known unauthorized use or disclosure of the Confidential Information and will
                cooperate with Company in any litigation brought by Company against third parties to protect its
                proprietary rights. License agrees not to modify, alter, decompile, disassemble, reverse engineer or
                emulate the functionality, reverse compile or otherwise reduce to human readable form, or create
                derivative works of the Software. Both parties agree to protect Confidential Information.
                {'\n'}
                {'\n'}
                <strong>15. FEEDBACK</strong>
                {'\n'}
                Customer grants Company a perpetual, irrevocable license to use any feedback or suggestions related to the
                Platform.
                {'\n'}
                {'\n'}
                <strong>16. SUSPENSION OF SERVICE</strong>
                {'\n'}
                Company may suspend access if Customer violates this Agreement, does not pay outstanding fees, Customer’s
                usage of the Platform threatens Platform security or any unauthorized activity is detected.
                {'\n'}
                {'\n'}
                <strong>17. TERM AND TERMINATION</strong>
                {'\n'}
                This Agreement remains in effect during the Subscription Term. Company may terminate the Agreement
                immediately if Customer breaches the Agreement, uses the Platform illegally or violates the Acceptable Use
                Policy. Upon termination, Customer shall immediately cease use of the Software.
                {'\n'}
                {'\n'}
                <strong>18. GOVERNING LAW</strong>
                {'\n'}
                This Agreement will be construed under the laws of the State of California, except for that body of law
                dealing with conflicts of law and the U.N. Convention of Contracts for the Sale of International Goods,
                which shall not apply. In the event there is a dispute concerning the subject matter of this Agreement,
                the proper venue shall be the County of Alameda, State of California, United States of America. Each party
                hereby waives opposition to jurisdiction in such court. Service of process can be done in accordance with
                the governing law of the Agreement. Performance of any obligation required by a party hereunder may be
                waived only by a written waiver signed by the other party, which waiver shall be effective only with
                respect to the specific obligation described therein. If any provision of this Agreement shall be held by
                a court of competent jurisdiction to be contrary to law, that provision will be enforced to the maximum
                extent permissible, and the remaining provisions of this Agreement will remain in full force and effect.
                {'\n'}
                {'\n'}
                <strong>19. EXPORT CONTROL</strong>
                {'\n'}
                Customer shall not export or re-export, or allow the export or re-export of the Software or any copy,
                portion or direct product of the foregoing, in violation of any export laws, restrictions, national
                security controls or regulations of the United States or other applicable foreign agency or authority.
                {'\n'}
                {'\n'}
                <strong>20. FORCE MAJEURE</strong>
                {'\n'}
                Neither party shall be liable for failure to perform due to events beyond reasonable control including
                cyber attacks, natural disasters, government actions, infrastructure or software outages, war, internet
                failures or such other unforeseen events.
                {'\n'}
                {'\n'}
                <strong>21. ASSIGNMENT</strong>
                {'\n'}
                Customer may not assign this Agreement without Company’s explicit written consent.
                {'\n'}
                {'\n'}
                <strong>22. ENTIRE AGREEMENT</strong>
                {'\n'}
                This Agreement constitutes the entire agreement between the parties. It supersedes all prior agreements
                relating to the Platform.
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  marginBottom: '18px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={eulaAccepted}
                  onChange={(e) => setEulaAccepted(e.target.checked)}
                  style={{
                    cursor: 'pointer',
                    width: '14px',
                    height: '14px',
                  }}
                />
                <span>I have read and accept the End User License Agreement.</span>
              </label>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowEula(false);
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: '1px solid var(--card-border, #1f2937)',
                    background: 'transparent',
                    color: 'var(--card-text, #e5e7eb)',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={performSignup}
                  disabled={!eulaAccepted || loading}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: 'none',
                    background:
                      !eulaAccepted || loading ? '#4b5563' : '#2563eb',
                    color: '#ffffff',
                    cursor:
                      !eulaAccepted || loading ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
